/**
 * The thread — `docs/activity.md` §7.1, §7.8, §7.9 (Boards 7, 8 A–C).
 *
 * A white head — the name, *<number> · Text* or *<email> · Email*,
 * *Reschedule today's visit* when the contact has a site visit today, and
 * **Attached to** with **Change**. Then the messages grouped by day: inbound
 * white on the left, outbound `accent-soft` on the right with *<time> ·
 * <person>*, automatic ones grey with *<time> · automatic · <what>*; emails
 * are cards. Nobody's conversation carries the amber band and **Take it ·
 * Assign to… · Add as a contact · New request from this**; a teammate's
 * carries *<Owner>'s conversation* and **Take over**.
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import {
  formatPhone, openedTimes, openedTitle, type ConversationThread, type EmailOpened, type ThreadFile, type ThreadMessage,
} from '@rowted/shared'
import { ContactForm } from '../../components/forms/contact-form.js'
import { Popover } from '../../components/overlays.js'
import { openNewRequest } from '../../requests/open-request.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { Face, type FacePerson } from '../faces.js'
import { Glyph } from '../glyphs.js'
import { useActivity } from '../activity-context.js'
import { moveItem } from './actions.js'
import { EmailBody } from './email-body.js'
import { FollowingLine } from './followers.js'
import { LinksEditor } from './links-editor.js'
import {
  BUTTON, PLAIN, PRIMARY, RecordChip, cardTimeWords, clockWords, dayHeading, errorWords, localOf,
} from './look.js'

/** *(717) 555-0193 · Text*, *dana.okafor@example.com · Email*, *to (717) 555-0142 · not a contact yet*. */
export function threadSubLine(thread: ConversationThread): string {
  const { row, composer, contact } = thread
  if (row.unknown) {
    const to = row.lastChannel === 'email' ? null : composer.smsFrom ? formatPhone(composer.smsFrom) : null
    return to ? `to ${to} · not a contact yet` : 'not a contact yet'
  }
  if (row.lastChannel === 'email') {
    const address = composer.email ?? contact?.email
    return address ? `${address} · Email` : 'Email'
  }
  const number = composer.mobiles[0]?.number ?? contact?.phone
  return number ? `${number} · Text` : 'Text'
}

/** The owner's face, from the people lists when they are in them. */
export function useOwnerFace(thread: ConversationThread | null): FacePerson | null {
  const { directory } = useActivity()
  if (!thread?.owner) return null
  const known = directory?.people.find((person) => person.id === thread.owner!.id)
  if (known) return known
  return { id: thread.owner.id, name: thread.owner.name, initials: thread.row.ownerInitials ?? '', colour: null }
}

// ---------------------------------------------------------------------------
// The head's three shapes
// ---------------------------------------------------------------------------

/** **Assign to…** — the people with inbox scope over its branch (§7.9). */
export function AssignMenu({
  thread, anchor, onClose, onDone,
}: { thread: ConversationThread; anchor: HTMLElement | null; onClose: () => void; onDone: () => void }) {
  const { directory } = useActivity()
  const [error, setError] = useState<string | null>(null)
  const give = async (userId: string) => {
    try {
      await activityRequest(`/conversations/${thread.id}/assign`, { method: 'POST', body: { userId } })
      onClose()
      onDone()
    } catch (cause) {
      setError(errorWords(cause))
    }
  }
  return (
    <Popover open onClose={onClose} anchor={anchor} width={260} label="Assign to" className="py-1.5">
      {thread.assignable.map((person) => {
        const face = directory?.people.find((one) => one.id === person.id)
        return (
          <button key={person.id} type="button" onClick={() => void give(person.id)}
            className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] hover:bg-hover">
            <Face person={face ?? { id: person.id, name: person.name, initials: person.name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2), colour: null }} size={22} />
            <span className="truncate">{person.name}</span>
          </button>
        )
      })}
      {error && <div className="px-3 py-1.5 text-[12px] text-danger">{error}</div>}
    </Popover>
  )
}

/** Nobody's yet (Board 8 B): the amber band and four ways to make it somebody's. */
export function UnassignedBlock({
  thread, onTaken, onReplaced,
}: { thread: ConversationThread; onTaken: () => void; onReplaced: (conversationId: string) => void }) {
  const { me } = useActivity()
  const [assignAt, setAssignAt] = useState<HTMLElement | null>(null)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canAddContact = thread.row.unknown && (me.permissions.contacts === 'edit' || me.permissions.contacts === 'full')

  const take = async () => {
    try {
      await activityRequest(`/conversations/${thread.id}/take`, { method: 'POST', body: {} })
      onTaken()
    } catch (cause) {
      setError(errorWords(cause))
    }
  }
  const giveContact = async (contactId: string) => {
    setAdding(false)
    try {
      const answer = await activityRequest<{ conversationId: string }>(`/conversations/${thread.id}/contact`, {
        method: 'POST', body: { contactId },
      })
      onReplaced(answer.conversationId)
    } catch (cause) {
      setError(errorWords(cause))
    }
  }

  return (
    <>
      <div className="h-2.5" />
      <div className="flex items-start gap-[9px] rounded-[10px] border border-amber-band-line bg-amber-band-bg px-3 py-2.5 text-[12px] leading-[1.45] text-amber-band-text">
        <Glyph name="info" size={15} />
        <div>
          {thread.row.unknown
            ? 'Nobody owns this yet: it came to the company’s number from someone Rowted doesn’t know.'
            : 'Nobody owns this yet.'}
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {thread.canTake && (
          <button type="button" onClick={() => void take()} className={cn(BUTTON, PRIMARY, 'h-8 px-3.5 text-[12.5px]')}>
            <Glyph name="check" size={14.5} />Take it
          </button>
        )}
        {thread.assignable.length > 0 && (
          <button type="button" onClick={(event) => setAssignAt(event.currentTarget)} className={cn(BUTTON, PLAIN, 'h-8 px-3.5 text-[12.5px]')}>
            <Glyph name="user" size={14.5} />Assign to…
          </button>
        )}
        {canAddContact && (
          <button type="button" onClick={() => setAdding(true)} className={cn(BUTTON, PLAIN, 'h-8 px-3.5 text-[12.5px]')}>
            <Glyph name="plus" size={14.5} />Add as a contact
          </button>
        )}
        <button type="button" onClick={() => openNewRequest(thread.row.contactId ? { contactId: thread.row.contactId }
          : thread.row.unknown ? { search: thread.row.name } : null)}
          className={cn(BUTTON, PLAIN, 'h-8 px-3.5 text-[12.5px]')}>
          <Glyph name="plus" size={14.5} />New request from this
        </button>
      </div>
      {error && <div className="mt-2 text-[12px] text-danger">{error}</div>}
      {assignAt && <AssignMenu thread={thread} anchor={assignAt} onClose={() => setAssignAt(null)} onDone={onTaken} />}
      <ContactForm open={adding} onClose={() => setAdding(false)} orgUnitId={thread.row.orgUnitId}
        initialPhone={thread.row.unknown && !thread.row.name.includes('@') ? thread.row.name : undefined}
        initialEmail={thread.row.unknown && thread.row.name.includes('@') ? thread.row.name : undefined}
        onSaved={(contactId) => void giveContact(contactId)} onOpenExisting={(contactId) => void giveContact(contactId)} />
    </>
  )
}

/** A teammate's (Board 8 C): whose it is, **Take over**, and what replying here does. */
export function TeammateBlock({ thread, onTaken }: { thread: ConversationThread; onTaken: () => void }) {
  const { me } = useActivity()
  const face = useOwnerFace(thread)
  const [error, setError] = useState<string | null>(null)
  if (!thread.owner) return null
  const owner = thread.owner
  const take = async () => {
    try {
      await activityRequest(`/conversations/${thread.id}/take`, { method: 'POST', body: {} })
      onTaken()
    } catch (cause) {
      setError(errorWords(cause))
    }
  }
  const byText = thread.composer.channels.includes('sms')
  return (
    <>
      <div className="h-2.5" />
      <div className="flex items-center gap-2">
        {face && <Face person={face} size={26} />}
        <div className="flex min-w-0 flex-col">
          <b className="truncate text-[13px]">{owner.name}’s conversation</b>
          <span className="text-[11.5px] text-text-muted">
            {/* Copied on one of its emails, it is in your Inbox (Aaron, Oct 26). */}
            {thread.following ? 'you’re following it' : `you can see ${owner.firstName}’s inbox`}
          </span>
        </div>
        <span className="flex-1" />
        {thread.canTake && (
          <button type="button" onClick={() => void take()} className={cn(BUTTON, PLAIN, 'h-[30px] px-3.5 text-[12px]')}>
            <Glyph name="swap" size={14} />Take over
          </button>
        )}
      </div>
      <div className="h-2.5" />
      <div className="rounded-[10px] bg-surface-alt px-3 py-2.5 text-[12px] leading-[1.45] text-text-muted">
        {/* Board 8 C: the names are bold in the note's own grey. */}
        Replying here sends as <b>{me.user.name}</b>
        {byText ? ' from the company’s number' : ''}; {owner.firstName} sees it in the thread.{' '}
        {thread.canTake && <><b>Take over</b> makes it yours — the next reply lands with you.</>}
      </div>
      {error && <div className="mt-2 text-[12px] text-danger">{error}</div>}
    </>
  )
}

/** The *Attached to* line and its **Change** (§7.8). */
export function AttachedLine({
  thread, onSaved, sideways = false, showChange = true,
}: { thread: ConversationThread; onSaved: () => void; sideways?: boolean; showChange?: boolean }) {
  const [at, setAt] = useState<HTMLElement | null>(null)
  return (
    <div className={cn('flex items-center gap-1.5 text-[11.5px] text-text-muted', sideways ? 'no-scrollbar overflow-x-auto' : 'flex-wrap')}>
      <span className="whitespace-nowrap">Attached to</span>
      {thread.links.length === 0 && <span className="text-text-faint">nothing yet</span>}
      {thread.links.map((link) => <RecordChip key={`${link.type}:${link.id}`} type={link.type} label={link.label} />)}
      {showChange && (
        <button type="button" onClick={(event) => setAt(event.currentTarget)}
          className="inline-flex shrink-0 items-center gap-[3px] font-semibold text-accent hover:text-accent-dark">
          <Glyph name="pencil" size={11} />Change
        </button>
      )}
      {at && <LinksEditor thread={thread} anchor={at} onClose={() => setAt(null)} onSaved={onSaved} />}
    </div>
  )
}

export function ThreadHead({
  thread, onChanged, onReplaced, onLeft = onChanged,
}: {
  thread: ConversationThread; onChanged: () => void; onReplaced: (conversationId: string) => void
  /** The viewer took themselves off a conversation they had only by following it. */
  onLeft?: () => void
}) {
  const unassigned = thread.owner === null
  const teammate = !unassigned && !thread.mine
  return (
    <div className="border-b border-line bg-surface px-[18px] py-3">
      <div className="flex items-center gap-2">
        <b className="truncate text-[15px]">{thread.row.name}</b>
        <span className="truncate text-[12px] text-text-muted">{threadSubLine(thread)}</span>
        <span className="flex-1" />
        {thread.todayVisitId && (
          <button type="button" onClick={() => void moveItem('appointment', thread.todayVisitId!)}
            className={cn(BUTTON, PLAIN, 'h-[30px] px-3.5 text-[12px]')}>
            <Glyph name="calendar" size={14} />Reschedule today’s visit
          </button>
        )}
      </div>
      {!thread.row.unknown && (
        <>
          <div className="h-2" />
          <AttachedLine thread={thread} onSaved={onChanged} />
        </>
      )}
      <FollowingLine thread={thread} onChanged={onChanged} onLeft={onLeft} />
      {unassigned && <UnassignedBlock thread={thread} onTaken={onChanged} onReplaced={onReplaced} />}
      {teammate && <TeammateBlock thread={thread} onTaken={onChanged} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The messages
// ---------------------------------------------------------------------------

const isImage = (file: ThreadFile): boolean => file.type === 'photo' || file.contentType.startsWith('image/')

function FileChip({ file }: { file: ThreadFile }) {
  return (
    <a href={`/api/v1/files/${file.id}`} target="_blank" rel="noreferrer"
      className="inline-flex h-[30px] max-w-full items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 text-[12px] font-semibold text-text hover:bg-hover">
      <span className={cn('inline-flex', file.contentType === 'application/pdf' ? 'text-danger' : 'text-text-muted')}>
        <Glyph name="file" size={13} />
      </span>
      <span className="truncate">{file.name}</span>
    </a>
  )
}

/** Photos in a text: 120 px thumbnails that open full size (§7.1). */
function Photos({ files, end }: { files: ThreadFile[]; end: boolean }) {
  if (files.length === 0) return null
  return (
    <div className={cn('mb-1 flex max-w-[420px] flex-wrap gap-1.5', end && 'justify-end')}>
      {files.map((file) => (
        <a key={file.id} href={`/api/v1/files/${file.id}`} target="_blank" rel="noreferrer" aria-label={file.name}
          className="block h-[120px] w-[120px] overflow-hidden rounded-[10px] border border-line bg-surface-alt">
          <img src={`/api/v1/files/${file.id}`} alt={file.name} loading="lazy" className="h-full w-full object-cover" />
        </a>
      ))}
    </div>
  )
}

function DayLine({ children }: { children: ReactNode }) {
  return (
    <div className="mb-0.5 mt-3.5 flex items-center gap-2.5 text-[11px] font-semibold text-text-faint">
      <div className="h-px flex-1 bg-line-faint" />{children}<div className="h-px flex-1 bg-line-faint" />
    </div>
  )
}

const FAILED = new Set(['failed', 'review', 'undelivered', 'bounced'])

/** An open's moment in the thread's own words, mid-sentence: *2:14 PM*, *yesterday 7:12 PM*, *Tue 3:30 PM*. */
const openedAt = (iso: string, today: string, timeZone: string): string =>
  cardTimeWords(iso, today, timeZone).replace(/^Yesterday/, 'yesterday')

/** *Opened 2:14 PM · 3 times* — the latest open, and how many when more than one. */
export function openedLineWords(opened: EmailOpened, today: string, timeZone: string): string {
  return `Opened ${openedAt(opened.last, today, timeZone)}${openedTimes(opened.count)}`
}

/**
 * Under an email we sent that its customer opened (Aaron, Oct 26: "does this
 * mean we can show when someone opens our emails in the app???"): the quiet
 * line, with where it was opened and the honesty on hover — *Opened means the
 * email's pictures loaded…* — because Apple Mail opens email by itself and a
 * mail app that blocks pictures never says. A phone has no hover, so the line
 * is a button there that says the same under it, with a 44 px hit area that
 * overlaps the card's padding rather than growing the card.
 */
export function OpenedLine({
  opened, today, timeZone, phone,
}: { opened: EmailOpened; today: string; timeZone: string; phone: boolean }) {
  const [told, setTold] = useState(false)
  const words = openedLineWords(opened, today, timeZone)
  const title = openedTitle(opened, (iso) => openedAt(iso, today, timeZone))
  if (!phone) {
    return <div data-opened title={title} className="mt-1.5 text-[11px] text-text-muted">{words}</div>
  }
  return (
    <div data-opened className="mt-1.5 text-[11px] text-text-muted">
      <button type="button" title={title} aria-expanded={told} onClick={() => setTold((was) => !was)}
        className="-my-[14px] inline-flex min-h-11 items-center text-left">
        {words}
      </button>
      {told && <div className="mt-1 leading-[1.45]">{title}</div>}
    </div>
  )
}

function Bubble({
  message, timeZone, unread, phone,
}: { message: ThreadMessage; timeZone: string; unread: boolean; phone: boolean }) {
  const out = message.direction === 'outbound'
  const time = clockWords(localOf(message.at, timeZone).minutes)
  const meta = [time]
  if (out && message.automatic) {
    meta.push('automatic')
    if (message.what && !phone) meta.push(message.what)
  } else if (out && message.byName) {
    meta.push(message.byName.trim().split(/\s+/)[0] ?? message.byName)
  }
  if (!out && unread && !phone) meta.push('unread')
  const photos = message.files.filter(isImage)
  const others = message.files.filter((file) => !isImage(file))
  return (
    <div className={cn('mt-2.5 flex flex-col', out ? 'items-end' : 'items-start')}>
      <Photos files={photos} end={out} />
      {message.body && (
        /* Board 7: 420 of words inside 12 px of padding and a hairline — 446 across. */
        <div className={cn('max-w-[446px] whitespace-pre-wrap break-words border px-3 py-[9px] text-[13px] leading-[1.45] text-text',
          out ? 'rounded-[14px_14px_4px_14px]' : 'rounded-[14px_14px_14px_4px]',
          !out ? 'border-line bg-surface' : message.automatic ? 'border-line bg-surface-group' : 'border-accent-line bg-accent-soft')}>
          {message.body}
        </div>
      )}
      {others.length > 0 && (
        <div className={cn('mt-1.5 flex max-w-[420px] flex-wrap gap-1.5', out && 'justify-end')}>
          {others.map((file) => <FileChip key={file.id} file={file} />)}
        </div>
      )}
      <span className="mt-[3px] text-[10.5px] text-text-faint">
        {meta.join(' · ')}
        {FAILED.has(message.status) && <span className="font-semibold text-danger"> · not sent</span>}
      </span>
    </div>
  )
}

/**
 * An email is a card (Board 8 A): sender, *Outlook*, *to <name>*, subject,
 * body, attachments. One that arrived as HTML draws it as it was sent
 * (§7.10, `email-body.tsx`), and the images it draws in place are not listed
 * again as attachments.
 */
function EmailCard({
  message, thread, today, phone,
}: { message: ThreadMessage; thread: ConversationThread; today: string; phone: boolean }) {
  const out = message.direction === 'outbound'
  const sender = out ? (message.byName ?? (message.automatic ? 'Sent automatically' : thread.owner?.name ?? '')) : thread.row.name
  const recipient = out ? thread.row.name : thread.owner?.name ?? message.to ?? ''
  const inline = new Set(message.html?.inline ?? [])
  const files = message.files.filter((file) => !inline.has(file.id))
  return (
    <div className={cn('mt-2.5 rounded-xl border bg-surface px-3.5 py-3', out ? 'border-accent-line' : 'border-line')}>
      <div className="flex items-center gap-1.5">
        <b className="truncate text-[13px]">{sender}</b>
        {message.via === 'outlook' && (
          <span className="inline-flex h-[18px] items-center rounded px-1.5 text-[10px] font-semibold bg-info-bg text-info">Outlook</span>
        )}
        {message.automatic && message.what && (
          <span className="inline-flex h-[18px] items-center rounded bg-line-soft px-1.5 text-[10px] font-semibold text-text-muted">
            {message.what}
          </span>
        )}
        <span className="flex-1" />
        <span className="whitespace-nowrap text-[11.5px] text-text-faint">{cardTimeWords(message.at, today, thread.timeZone)}</span>
      </div>
      {recipient && (
        <div className="mt-0.5 truncate text-[11.5px] text-text-muted"
          title={message.cc.length > 0 || message.bcc.length > 0
            ? [message.cc.length > 0 ? `cc ${message.cc.join(', ')}` : '', message.bcc.length > 0 ? `bcc ${message.bcc.join(', ')}` : '']
              .filter(Boolean).join(' · ')
            : undefined}>
          to {recipient}{message.cc.length > 0 ? ` · cc ${message.cc.join(', ')}` : ''}
          {/* Inside only: the customer's copy never says who else had it (Aaron, Oct 26). */}
          {message.bcc.length > 0 ? ` · bcc ${message.bcc.join(', ')}` : ''}
        </div>
      )}
      {message.subject && <div className="mt-2 text-[12.5px] font-semibold">{message.subject}</div>}
      {message.html
        ? <EmailBody conversationId={thread.id} message={message} sender={sender} unknownSender={thread.row.unknown} />
        : <div className="mt-1.5 whitespace-pre-wrap break-words text-[13px] leading-[1.5] text-text-body">{message.body}</div>}
      {files.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {files.map((file) => <FileChip key={file.id} file={file} />)}
        </div>
      )}
      {FAILED.has(message.status)
        ? <div className="mt-1.5 text-[11px] font-semibold text-danger">Not sent</div>
        : out && message.opened
          ? <OpenedLine opened={message.opened} today={today} timeZone={thread.timeZone} phone={phone} />
          : null}
    </div>
  )
}

/**
 * The messages, oldest first, with the newest in view. A text starts a new
 * day with its day line (*Tue, Oct 20*, *Today*); an email card carries its
 * own day and time, so it never needs one (Board 8 A).
 */
export function Messages({
  thread, today, unreadAtOpen, phone = false, className,
}: { thread: ConversationThread; today: string; unreadAtOpen: boolean; phone?: boolean; className?: string }) {
  const box = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  /* Kept at the newest while nobody has scrolled up — an email drawn late (§7.10) grows the thread under it. */
  const stick = useRef(true)
  const lastInbound = unreadAtOpen
    ? [...thread.messages].reverse().find((message) => message.direction === 'inbound')?.id ?? null : null

  useLayoutEffect(() => {
    const element = box.current
    stick.current = true
    if (element) element.scrollTop = element.scrollHeight
  }, [thread.id, thread.messages.length])

  useEffect(() => {
    const element = box.current
    const inner = content.current
    if (!element || !inner) return
    const watcher = new ResizeObserver(() => {
      if (stick.current) element.scrollTop = element.scrollHeight
    })
    watcher.observe(inner)
    return () => watcher.disconnect()
  }, [])

  let shownDay: string | null = null
  return (
    <div ref={box} className={cn('min-h-0 flex-1 overflow-y-auto', className)}
      onScroll={(event) => {
        const element = event.currentTarget
        stick.current = element.scrollTop + element.clientHeight >= element.scrollHeight - 8
      }}>
      <div ref={content}>
      {thread.messages.length === 0 && (
        <div className="py-10 text-center text-[12.5px] text-text-muted">No messages yet.</div>
      )}
      {thread.messages.map((message) => {
        let line: ReactNode = null
        if (message.channel === 'sms' && message.day !== shownDay) {
          shownDay = message.day
          line = <DayLine>{dayHeading(message.day, today)}</DayLine>
        }
        return (
          <Fragment key={message.id}>
            {line}
            {message.channel === 'email'
              ? <EmailCard message={message} thread={thread} today={today} phone={phone} />
              : <Bubble message={message} timeZone={thread.timeZone} unread={message.id === lastInbound} phone={phone} />}
          </Fragment>
        )
      })}
      </div>
    </div>
  )
}

/** Keeps *· unread* on the newest inbound message for as long as this thread stays open. */
export function useUnreadAtOpen(thread: ConversationThread | null): boolean {
  const [seen, setSeen] = useState<{ id: string; unread: boolean } | null>(null)
  useEffect(() => {
    if (thread && seen?.id !== thread.id) setSeen({ id: thread.id, unread: thread.row.unread })
  }, [thread, seen])
  return thread && seen?.id === thread.id ? seen.unread : Boolean(thread?.row.unread)
}
